import { useMutation } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import { useTranslation } from 'react-i18next'

export type PaymentType = 'scoping' | 'development'

interface CreateCheckoutParams {
  teamId: string
  projectId: string
  paymentType: PaymentType
  quoteId?: string // Required for development payments - the accepted quote ID
  currency?: string
}

interface CheckoutResponse {
  sessionId: string
  url: string
}

async function createCheckoutSession(params: CreateCheckoutParams): Promise<CheckoutResponse> {
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error('Not authenticated')
  }

  // Get the current language for Stripe locale
  const locale = document.documentElement.lang || 'en'

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        ...params,
        locale,
      }),
    }
  )

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to create checkout session')
  }

  return response.json()
}

export function useCreateCheckout() {
  const { i18n } = useTranslation()

  return useMutation({
    mutationFn: (params: CreateCheckoutParams) =>
      createCheckoutSession({
        ...params,
        currency: i18n.language === 'fr' ? 'EUR' : 'USD',
      }),
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url
      }
    },
  })
}
